<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'worksheet',
    path: '/worksheets/:id',
    component: 'Worksheet',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Worksheet',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'full-screen',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = () => {
    return props.oid === 'create' || props.oid == null
      ? $fn.value.fetch('get_worksheet_template', { asResource: true })
      : $fn.value.fetch('read_worksheet', {
          parameters: { id: props.oid },
          asResource: true,
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { item: $$load_item },
    ux: {},
    dataRefs: {
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isNew = computed(() => {
    if (loaded.value) {
      return item.value.isNew;
    }
    return null;
  });
  const isReadonly = computed(() => {
    return false;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('number'), $fn.value.gettext('Worksheet')],
        icon: $config.value.worksheet.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_48uyrfgeq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadGrid_9i6xqrof0_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'worksheets',
    };
  };
  const act_1040167445267876_reloadGrid_9ydz36deb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'worksheets',
    };
  };
  const inst_evh_1040167445267876_success_fyk97ug81_getWhen = async (
    $event
  ) => {
    return item.value.id;
  };
  const act_1040167445267876_closeComponent_n1ktpmiu4_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const inst_evh_1040167445267876_success_km5nk770h_getWhen = async (
    $event
  ) => {
    return !item.value.id;
  };
  const act_1040167445267876_navigate_3umuotm17_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.worksheet',
        params: {
          id: $event.data.id,
        },
      },
    };
  };
  const inst_evh_1040167445267876_success_k6scijcbr_getWhen = async (
    $event
  ) => {
    return !item.value.id;
  };
  const act_1040167445267876_setData_661obrbcg_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_1040167445267876_success_yqf4g8pt4_getWhen = async (
    $event
  ) => {
    return item.value.id && !!$event.data && !!$event.data.id;
  };
  const act_1040167445267876_form_jj540jh0w_getActionArgs = async ($event) => {
    return {
      name: props.formId,
      reset: true,
    };
  };
  const inst_evh_1040167445267876_success_ol8l9ops8_getWhen = async (
    $event
  ) => {
    return item.value.id && !!$event.data && !!$event.data.id;
  };
  const act_1040167445267876_crud_64ec522nq_getActionArgs = async ($event) => {
    return {
      objectType: 'worksheet',
      op: 'upsert',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_48uyrfgeq =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_48uyrfgeq_getActionArgs,
    });
  const inst_evh_7315092382398562_close_a4nadvbx4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_48uyrfgeq,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_a4nadvbx4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_p12n59jqt =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_pdkskuics =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_p12n59jqt,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_pdkskuics],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_0l4ygf4kw =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_hw63anzyk =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_0l4ygf4kw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_hw63anzyk],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_u1fxsnscn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_so5h9ipjd =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_u1fxsnscn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_so5h9ipjd],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_94oddmhxa =
    new actionTypes.CloseComponentAction({});
  const inst_evh_6466705138206980_deleted_4m2o7cwgr =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_94oddmhxa,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadGrid_9i6xqrof0 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6466705138206980_reloadGrid_9i6xqrof0_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_rcip0yki9 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadGrid_9i6xqrof0,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_4m2o7cwgr,
      inst_evh_6466705138206980_deleted_rcip0yki9,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_reloadGrid_9ydz36deb =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_9ydz36deb_getActionArgs,
    });
  const inst_evh_1040167445267876_success_fyk97ug81 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_9ydz36deb,
      event: 'success',
      when: inst_evh_1040167445267876_success_fyk97ug81_getWhen,
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_closeComponent_n1ktpmiu4 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_n1ktpmiu4_getActionArgs,
    });
  const inst_evh_1040167445267876_success_km5nk770h =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_n1ktpmiu4,
      event: 'success',
      when: inst_evh_1040167445267876_success_km5nk770h_getWhen,
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_3umuotm17 =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_3umuotm17_getActionArgs,
    });
  const inst_evh_1040167445267876_success_k6scijcbr =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_3umuotm17,
      event: 'success',
      when: inst_evh_1040167445267876_success_k6scijcbr_getWhen,
      displayName: 'navigate',
    });
  const act_1040167445267876_setData_661obrbcg = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_661obrbcg_getActionArgs,
  });
  const inst_evh_1040167445267876_success_yqf4g8pt4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_661obrbcg,
      event: 'success',
      when: inst_evh_1040167445267876_success_yqf4g8pt4_getWhen,
      displayName: 'setData',
    });
  const act_1040167445267876_form_jj540jh0w = new actionTypes.FormAction({
    actionArgs: act_1040167445267876_form_jj540jh0w_getActionArgs,
  });
  const inst_evh_1040167445267876_success_ol8l9ops8 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_form_jj540jh0w,
      event: 'success',
      when: inst_evh_1040167445267876_success_ol8l9ops8_getWhen,
      displayName: 'form',
    });
  const act_1040167445267876_crud_64ec522nq = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_64ec522nq_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_success_fyk97ug81,
      inst_evh_1040167445267876_success_km5nk770h,
      inst_evh_1040167445267876_success_k6scijcbr,
      inst_evh_1040167445267876_success_yqf4g8pt4,
      inst_evh_1040167445267876_success_ol8l9ops8,
    ],
  });
  const inst_evh_1040167445267876_submit_joaocf5vy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_64ec522nq,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_joaocf5vy],
    event: 'submit',
  });
  const act_1468474219929533_reloadComponentData_f90rup3hd =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1468474219929533_reload_b8em0khl5 =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_reloadComponentData_f90rup3hd,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1468474219929533_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_reload_b8em0khl5],
    event: 'reload',
  });
  const act_1306227577716690_reloadComponentData_2bupxsxaw =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1306227577716690_reload_o4weeh9al =
    new actionTypes.EventHandlerImpl({
      action: act_1306227577716690_reloadComponentData_2bupxsxaw,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1306227577716690_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1306227577716690_reload_o4weeh9al],
    event: 'reload',
  });
  const act_8723442106880637_reloadComponentData_k9sxkjciz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8723442106880637_reload_h6u1iocy9 =
    new actionTypes.EventHandlerImpl({
      action: act_8723442106880637_reloadComponentData_k9sxkjciz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8723442106880637_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8723442106880637_reload_h6u1iocy9],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_a4nadvbx4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_pdkskuics,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_hw63anzyk,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_so5h9ipjd,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_4m2o7cwgr,
    inst_evh_6466705138206980_deleted_rcip0yki9,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_fyk97ug81,
    inst_evh_1040167445267876_success_km5nk770h,
    inst_evh_1040167445267876_success_k6scijcbr,
    inst_evh_1040167445267876_success_yqf4g8pt4,
    inst_evh_1040167445267876_success_ol8l9ops8,
    inst_evh_1040167445267876_submit_joaocf5vy,
    save: inst_evh_1040167445267876_submit_joaocf5vy,
    inst_evh_1040167445267876_submit,
    inst_evh_1468474219929533_reload_b8em0khl5,
    inst_evh_1468474219929533_reload,
    inst_evh_1306227577716690_reload_o4weeh9al,
    inst_evh_1306227577716690_reload,
    inst_evh_8723442106880637_reload_h6u1iocy9,
    inst_evh_8723442106880637_reload,
  };

  const evh_7315092382398562_close_a4nadvbx4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_a4nadvbx4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_pdkskuics = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_pdkskuics.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_hw63anzyk = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_hw63anzyk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_so5h9ipjd = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_so5h9ipjd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_4m2o7cwgr = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_4m2o7cwgr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_rcip0yki9 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_rcip0yki9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_fyk97ug81 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_fyk97ug81.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_km5nk770h = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_km5nk770h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_k6scijcbr = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_k6scijcbr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_yqf4g8pt4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_yqf4g8pt4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ol8l9ops8 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ol8l9ops8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_joaocf5vy = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_joaocf5vy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_reload_b8em0khl5 = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_reload_b8em0khl5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_reload = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_reload_o4weeh9al = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_reload_o4weeh9al.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1306227577716690_reload = async (event, scope) => {
    return await actions.inst_evh_1306227577716690_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8723442106880637_reload_h6u1iocy9 = async (event, scope) => {
    return await actions.inst_evh_8723442106880637_reload_h6u1iocy9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8723442106880637_reload = async (event, scope) => {
    return await actions.inst_evh_8723442106880637_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    card
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Worksheet"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.worksheet.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcWorksheetHeader
          :worksheet="item"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcWorksheetHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="worksheet"
        autofocus="first"
        v-if="isNew"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <UcWorksheetEditorFieldset :worksheet="item">
          </UcWorksheetEditorFieldset>
        </ItpCol>
      </ItpForm>
      <ItpPanel
        collapsible
        :caption="pgettext('Worksheet', 'Worksheet data')"
        carousel
        v-if="!isNew"
      >
        <UcWorksheetData
          :worksheet="item"
          :formId="formId"
          :reload="lastLoaded"
          class="pt-3"
          @reload="evh_1468474219929533_reload($event, {})"
        >
        </UcWorksheetData>
      </ItpPanel>
      <hr v-if="!isNew" />
      <div class="overflow-hidden w-100" v-if="!isNew">
        <UcWorksheetWorkItems
          :worksheet="item"
          :isReadonly="isReadonly"
          :reload="lastLoaded"
          class="h-100 w-100"
          @reload="evh_1306227577716690_reload($event, {})"
        >
        </UcWorksheetWorkItems>
      </div>
      <hr v-if="!isNew" />
      <div class="overflow-hidden w-100" v-if="!isNew">
        <UcWorksheetInvoiceLineAccountings
          :worksheet="item"
          :isReadonly="isReadonly"
          :reload="lastLoaded"
          class="h-100 w-100"
          @reload="evh_8723442106880637_reload($event, {})"
        >
        </UcWorksheetInvoiceLineAccountings>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('Button', 'Create worksheet')"
          type="submit"
          :form="forms.worksheet"
          variant="primary"
          :spinning="actions.save.isRunning"
          class="me-2"
          v-if="isNew"
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
